import type { Appearance } from '@clerk/types';

type ElementClassMap = Record<string, unknown>;

const baseElements: ElementClassMap = {
  rootBox: 'w-full',
  cardBox: 'w-full max-w-none shadow-none',
  card: 'border border-border bg-card text-card-foreground shadow-sm rounded-lg',
  headerTitle: 'text-2xl font-semibold tracking-tight text-foreground',
  headerSubtitle: 'text-muted-foreground',
  dividerLine: 'bg-border',
  dividerText: 'text-muted-foreground',
  formFieldLabel: 'text-foreground',
  formFieldInput:
    'rounded-md border border-input bg-background text-foreground placeholder:text-muted-foreground focus:border-ring focus:ring-2 focus:ring-ring/20',
  formFieldInputShowPasswordButton: 'text-muted-foreground hover:text-foreground',
  formFieldAction: 'text-primary hover:text-primary/80',
  formFieldHintText: 'text-muted-foreground',
  formFieldErrorText: 'text-destructive',
  formButtonPrimary:
    'rounded-md bg-primary font-medium text-primary-foreground shadow-none transition-colors hover:bg-primary/90 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
  socialButtonsBlockButton:
    'rounded-md border border-border bg-background text-foreground shadow-none transition-colors hover:bg-muted',
  socialButtonsBlockButtonText: 'font-medium text-foreground',
  footer: 'text-muted-foreground',
  footerActionText: 'text-muted-foreground',
  footerActionLink: 'font-medium text-primary hover:text-primary/80',
  footerPagesLink: 'text-muted-foreground hover:text-foreground',
  userButtonAvatarBox: 'h-9 w-9 rounded-md',
  userButtonPopoverCard:
    'rounded-lg border border-border bg-popover text-popover-foreground shadow-lg',
  userButtonPopoverMain: 'bg-popover',
  userButtonPopoverActionButton:
    'text-popover-foreground transition-colors hover:bg-muted hover:text-foreground',
  userButtonPopoverActionButtonIcon: 'text-muted-foreground',
  userButtonPopoverFooter: 'border-t border-border bg-popover text-muted-foreground',
  navbar: 'border-r border-border bg-card',
  navbarButton:
    'rounded-md text-muted-foreground hover:bg-muted hover:text-foreground data-[active=true]:bg-primary/10 data-[active=true]:text-primary',
  profileSection: 'border-border',
  profileSectionTitleText: 'text-foreground',
  profileSectionSubtitleText: 'text-muted-foreground',
  profileSectionPrimaryButton:
    'rounded-md bg-primary text-primary-foreground hover:bg-primary/90',
};

const baseVariables = {
  colorPrimary: 'hsl(var(--primary))',
  colorPrimaryForeground: 'hsl(var(--primary-foreground))',
  colorBackground: 'hsl(var(--card))',
  colorForeground: 'hsl(var(--foreground))',
  colorText: 'hsl(var(--foreground))',
  colorMuted: 'hsl(var(--muted))',
  colorMutedForeground: 'hsl(var(--muted-foreground))',
  colorTextSecondary: 'hsl(var(--muted-foreground))',
  colorInput: 'hsl(var(--background))',
  colorInputBackground: 'hsl(var(--background))',
  colorInputForeground: 'hsl(var(--foreground))',
  colorInputText: 'hsl(var(--foreground))',
  colorBorder: 'hsl(var(--border))',
  colorRing: 'hsl(var(--ring))',
  colorDanger: 'hsl(var(--destructive))',
  colorShadow: 'hsl(var(--foreground) / 0.12)',
  borderRadius: '0.5rem',
  fontFamily: 'Instrument Sans, system-ui, -apple-system, sans-serif',
  fontFamilyButtons: 'Instrument Sans, system-ui, -apple-system, sans-serif',
};

const baseAppearance: Appearance = {
  layout: {
    logoPlacement: 'none',
    socialButtonsPlacement: 'bottom',
  },
  variables: baseVariables,
  elements: baseElements as NonNullable<Appearance['elements']>,
  captcha: {
    theme: 'auto',
  },
};

export const clerkAppearance = createClerkAppearance();

export function createClerkAppearance(overrides: Appearance = {}): Appearance {
  return {
    ...baseAppearance,
    ...overrides,
    layout: {
      ...baseAppearance.layout,
      ...overrides.layout,
    },
    variables: {
      ...baseAppearance.variables,
      ...overrides.variables,
    },
    elements: mergeElements(baseAppearance.elements, overrides.elements),
    captcha: {
      ...baseAppearance.captcha,
      ...overrides.captcha,
    },
  };
}

function mergeElements(
  base: Appearance['elements'],
  overrides?: Appearance['elements']
): Appearance['elements'] {
  const merged: ElementClassMap = { ...(base as ElementClassMap) };

  for (const [key, value] of Object.entries((overrides ?? {}) as ElementClassMap)) {
    const existing = merged[key];

    if (typeof existing === 'string' && typeof value === 'string') {
      merged[key] = `${existing} ${value}`;
      continue;
    }

    if (isRecord(existing) && isRecord(value)) {
      merged[key] = { ...existing, ...value };
      continue;
    }

    merged[key] = value;
  }

  return merged as Appearance['elements'];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
